import React from 'react';
import { authService } from '../services/authService';
import {
  hasPermission,
  hasModuleAccess,
  getUserHierarchyLevel,
  getUserAssignedRole,
} from '../utils/featureFlagsStore';

// Role hierarchy — mirrors backend OWNER / MANAGER / OPERATOR.
// OPERATOR users are normalised to 'Staff' in authService.normalizeRole.
export const ROLE_HIERARCHY = {
  'Owner': 3,
  'Manager': 2,
  'Staff': 1,
};

export type UserRole = 'Owner' | 'Manager' | 'Staff';

/**
 * Check if user has a specific role
 */
export function useHasRole(requiredRole: UserRole): boolean {
  const user = authService.getCurrentUser();
  if (!user) return false;
  return user.role === requiredRole;
}

/**
 * Check if user has at least the specified role level
 * (hierarchical: Owner > Manager > Staff)
 */
export function useHasMinimumRole(minRole: UserRole): boolean {
  const user = authService.getCurrentUser();
  if (!user) return false;
  const userLevel = ROLE_HIERARCHY[user.role as UserRole] || 0;
  const minLevel = ROLE_HIERARCHY[minRole] || 0;
  return userLevel >= minLevel;
}

/**
 * Check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  return authService.isAuthenticated();
}

/**
 * Get current user
 */
export function useGetCurrentUser() {
  return authService.getCurrentUser();
}

/**
 * Component wrapper for role-based access
 * Only renders children if user has the required role
 */
export const RoleGuard: React.FC<{
  requiredRole: UserRole | UserRole[];
  fallback?: React.ReactNode;
  children: React.ReactNode;
}> = ({
  requiredRole,
  fallback,
  children,
}) => {
  const user = authService.getCurrentUser();

  if (!user) {
    return <>{fallback || null}</>;
  }

  const rolesArray = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
  const hasRequiredRole = rolesArray.includes(user.role as UserRole);

  if (!hasRequiredRole) {
    return <>{fallback || null}</>;
  }

  return <>{children}</>;
};

/**
 * Check whether the current user holds a specific fine-grained permission code.
 * Falls back to coarse role check when no RBAC permissions are loaded.
 */
export function useHasPermission(code: string): boolean {
  return hasPermission(code);
}

/**
 * Check whether the current user has access to a module (has <module>.view).
 */
export function useHasModuleAccess(module: string): boolean {
  return hasModuleAccess(module);
}

/**
 * Return the current user's hierarchy level (1–4) or null if not loaded.
 */
export function useHierarchyLevel(): number | null {
  return getUserHierarchyLevel();
}

/**
 * Return the name of the current user's assigned role or null.
 */
export function useAssignedRoleName(): string | null {
  return getUserAssignedRole();
}

/**
 * Page access rules — used as a FALLBACK when no RBAC permissions are loaded.
 * Once a user has an assigned_role, module gating is handled by hasPermission().
 */
export const pageAccessRules: Record<string, UserRole[]> = {
  'dashboard':     ['Owner', 'Manager', 'Staff'],
  'gst':           ['Owner', 'Manager'],
  'inventory':     ['Owner', 'Manager', 'Staff'],
  'factory':       ['Owner', 'Manager', 'Staff'],
  'orders':        ['Owner', 'Manager', 'Staff'],
  'workforce':     ['Owner', 'Manager', 'Staff'],
  'assets':        ['Owner', 'Manager', 'Staff'],
  'finance':       ['Owner', 'Manager'],
  'reports':       ['Owner', 'Manager'],
  'settings':      ['Owner', 'Manager'],
  'approvals':     ['Owner', 'Manager', 'Staff'],
  'notifications': ['Owner', 'Manager', 'Staff'],
  'help':          ['Owner', 'Manager', 'Staff'],
};

/**
 * Check if user can access a page
 */
export function useCanAccessPage(pageName: keyof typeof pageAccessRules): boolean {
  const user = authService.getCurrentUser();
  if (!user) return false;

  const allowedRoles = pageAccessRules[pageName] || [];
  return allowedRoles.includes(user.role as UserRole);
}

export type PermissionAction = 'view' | 'create' | 'update' | 'delete' | 'approve' | 'adjust';

/**
 * Generic permission check matching backend HasActionPermission.
 */
export function canPerform(module: string, action: PermissionAction): boolean {
  const user = authService.getCurrentUser();
  if (!user) return false;
  if (user.role === 'Owner') return true;
  if (user.role === 'Manager' && action !== 'delete') return true;
  return hasPermission(`${module}.${action}`);
}

export function useCanPerform(module: string, action: PermissionAction): boolean {
  return canPerform(module, action);
}

export function useCanCreateOrder(): boolean {
  return canPerform('orders', 'create');
}

export function useCanUpdateOrder(): boolean {
  return canPerform('orders', 'update');
}

export function useCanCreateProduct(): boolean {
  return canPerform('inventory', 'create');
}

export function useCanCreateRawMaterial(): boolean {
  return canPerform('inventory', 'create');
}

export function useCanCreateBatch(): boolean {
  return canPerform('production', 'create');
}

export function useCanAdjustInventory(): boolean {
  return canPerform('inventory', 'adjust');
}

export function useCanCreateParty(): boolean {
  return canPerform('orders', 'create');
}

export function useCanCreateAsset(): boolean {
  return canPerform('assets', 'create');
}

export function useCanCreateProcessing(): boolean {
  return canPerform('processing', 'create');
}

export function useCanManageApprovalRules(): boolean {
  const user = authService.getCurrentUser();
  if (!user) return false;
  if (user.role === 'Owner' || user.role === 'Manager') return true;
  const level = getUserHierarchyLevel();
  return level !== null && level >= 3;
}

/**
 * Check if user can approve bills.
 * Owners always can; RBAC users with finance.approve permission can too.
 */
export function useCanApproveBills(): boolean {
  const user = authService.getCurrentUser();
  if (!user) return false;
  if (user.role === 'Owner') return true;
  return hasPermission('finance.approve');
}

/**
 * Check if the current user can create purchase bills.
 */
export function useCanCreateBill(): boolean {
  const user = authService.getCurrentUser();
  if (!user) return false;
  if (user.role === 'Owner' || user.role === 'Manager') return true;
  return hasPermission('finance.create');
}

/**
 * Check if the current user can create workers.
 */
export function useCanCreateWorker(): boolean {
  const user = authService.getCurrentUser();
  if (!user) return false;
  if (user.role === 'Owner' || user.role === 'Manager') return true;
  return hasPermission('workforce.create');
}

/**
 * Check if the current user can edit workers.
 */
export function useCanEditWorker(): boolean {
  const user = authService.getCurrentUser();
  if (!user) return false;
  if (user.role === 'Owner' || user.role === 'Manager') return true;
  return hasPermission('workforce.update');
}

/**
 * Check if the current user can mark attendance.
 */
export function useCanMarkAttendance(): boolean {
  const user = authService.getCurrentUser();
  if (!user) return false;
  if (user.role === 'Owner' || user.role === 'Manager') return true;
  return hasPermission('workforce.update');
}

/**
 * Check if user can change bill status.
 * Validates status transitions based on user role or RBAC finance.approve permission.
 */
export function useCanChangeBillStatus(
  fromStatus: string,
  toStatus: string
): boolean {
  const user = authService.getCurrentUser();
  if (!user) return false;

  const canApprove =
    user.role === 'Owner' ||
    user.role === 'Manager' ||
    hasPermission('finance.approve');

  if (!canApprove) return false;
  return isValidStatusTransition(fromStatus, toStatus);
}

/**
 * Validate if status transition is allowed
 */
function isValidStatusTransition(from: string, to: string): boolean {
  const validTransitions: Record<string, string[]> = {
    'PENDING': ['PAID', 'PARTIAL', 'CANCELLED'],
    'PARTIAL': ['PAID', 'CANCELLED'],
    'OVERDUE': ['PAID', 'PARTIAL', 'CANCELLED'],
    'DRAFT': ['PENDING', 'CANCELLED'],
    'SENT': ['PAID', 'PARTIAL', 'CANCELLED'], // For sales invoices
    'PENDING_APPROVAL': ['PAID', 'PENDING'], // Via approval/rejection only
    'PAID': ['CANCELLED'], // Allow cancellation of paid bills
    'CANCELLED': [] // No transitions from cancelled
  };

  return validTransitions[from]?.includes(to) || false;
}

/**
 * Check if user can change order status
 * Validates status transitions based on user role
 */
export function canChangeOrderStatus(
  fromStatus: string,
  toStatus: string
): boolean {
  const user = authService.getCurrentUser();
  if (!user) return false;

  if (!canPerform('orders', 'update')) {
    return false;
  }

  return isValidOrderStatusTransition(fromStatus, toStatus);
}

/**
 * Validate if order status transition is allowed
 */
function isValidOrderStatusTransition(from: string, to: string): boolean {
  // CANCELLED is terminal; DELIVERED may still be cancelled (e.g. outbound returns)
  const terminalStatuses = ['CANCELLED'];
  if (terminalStatuses.includes(from)) {
    return false;
  }

  if (from === 'DELIVERED' && to === 'CANCELLED') {
    return true;
  }

  if (from === 'DELIVERED') {
    return false;
  }

  // OPEN status can transition to any other status
  if (from === 'OPEN') {
    return true;
  }

  // All non-terminal statuses can transition to CANCELLED
  if (to === 'CANCELLED') {
    return true;
  }

  // Allow transitions between non-terminal statuses
  return true;
}
